import { VetService } from './vetService.js';
import { DaycareService } from './daycareService.js';
import { HotelService } from './hotelService.js';

const API_URL = process.env.REACT_APP_API_BASE_URL || '';

export class DashboardService {

    static async getTodaysVetAppointments(authorizedFetch) {
        try {
            const allAppointments = await VetService.getAllAppointments(authorizedFetch);
            const now = new Date();
            const todayString = [now.getFullYear(), String(now.getMonth() + 1).padStart(2, '0'), String(now.getDate()).padStart(2, '0')].join('-');

            return allAppointments.filter(appointment => {
                const dateValue = appointment.date || appointment.appointmentDate;
                if (!dateValue) return false;
                const d = new Date(dateValue);
                if (isNaN(d.getTime())) return false;
                return [d.getFullYear(), String(d.getMonth() + 1).padStart(2, '0'), String(d.getDate()).padStart(2, '0')].join('-') === todayString;
            });
        } catch (error) {
            console.error('Error fetching today\'s vet appointments:', error);
            return [];
        }
    }

    static async getUpcomingVaccinations(authorizedFetch) {
        try {
            const res = await authorizedFetch(`${API_URL}/api/vet/appointments/upcoming-vaccines`);
            if (!res.ok) throw new Error(await res.text());
            return await res.json();
        } catch (error) {
            console.error('Error fetching upcoming vaccinations:', error);
            return [];
        }
    }

    static async getUpcomingVetAppointments(authorizedFetch) {
        try {
            const allAppointments = await VetService.getAllAppointments(authorizedFetch);
            const today = new Date();
            const nextWeek = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);

            return allAppointments
                .filter(appointment => {
                    const dateValue = appointment.date || appointment.appointmentDate;
                    if (!dateValue) return false;
                    const d = new Date(dateValue);
                    if (isNaN(d.getTime())) return false;
                    return d >= today && d <= nextWeek;
                })
                .sort((a, b) => {
                    const dateA = new Date(a.date || a.appointmentDate);
                    const dateB = new Date(b.date || b.appointmentDate);
                    return dateA - dateB;
                });
        } catch (error) {
            console.error('Error fetching upcoming vet appointments:', error);
            return [];
        }
    }

    static async getDaycareCheckedInDogs(authorizedFetch) {
        try {
            const todaysVisits = await DaycareService.getTodaysVisits(authorizedFetch);
            return todaysVisits.filter(visit => visit.checkInTime && !visit.checkOutTime);
        } catch (error) {
            console.error('Error fetching daycare checked-in dogs:', error);
            return [];
        }
    }

    static async getTodayLunch(authorizedFetch) {
        try {
            const response = await authorizedFetch(`${API_URL}/api/dogs?hasLunchInstructions=true&limit=1000`);
            if (!response.ok) throw new Error(await response.text());
            const dogsData = await response.json();
            const dogs = dogsData.data ? dogsData.data : (Array.isArray(dogsData) ? dogsData : []);
            return dogs.map(dog => ({
                dogName: dog.name || 'Desconocido',
                breed: dog.breed || '',
                ownerName: dog.owners?.[0]?.name || '',
                instructions: dog.lunchInstructions || '',
                dogId: dog._id,
            }));
        } catch (error) {
            console.error('Error fetching lunch schedule:', error);
            return [];
        }
    }

    static async getTodayTransportation(authorizedFetch) {
        try {
            const response = await authorizedFetch(`${API_URL}/api/dogs?needsTransportation=true&limit=1000`);
            if (!response.ok) throw new Error(await response.text());
            const dogsData = await response.json();
            const dogs = dogsData.data ? dogsData.data : (Array.isArray(dogsData) ? dogsData : []);
            return dogs
                .map(dog => ({
                    dogName: dog.name || 'Desconocido',
                    breed: dog.breed || '',
                    ownerName: dog.owners?.[0]?.name || '',
                    pickUpTime: dog.transportation?.pickUpTime || '',
                    dropOffTime: dog.transportation?.dropOffTime || '',
                    notes: dog.transportation?.notes || '',
                    dogId: dog._id,
                }))
                .sort((a, b) => {
                    const ta = a.pickUpTime || a.dropOffTime || '';
                    const tb = b.pickUpTime || b.dropOffTime || '';
                    return ta.localeCompare(tb);
                });
        } catch (error) {
            console.error('Error fetching today transportation:', error);
            return [];
        }
    }

    static async getHotelOverview(authorizedFetch) {
        try {
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            // The overview only shows current and upcoming stays, so past
            // bookings are filtered out server-side rather than downloaded.
            const fromStr = [today.getFullYear(), String(today.getMonth() + 1).padStart(2, '0'), String(today.getDate()).padStart(2, '0')].join('-');
            const allBookings = await HotelService.getActiveBookings(fromStr, authorizedFetch);

            const upcomingBookings = [];
            const currentCheckIns = [];

            allBookings.forEach(booking => {
                const checkInDate = new Date(booking.checkIn);
                const checkOutDate = new Date(booking.checkOut);
                checkInDate.setHours(0, 0, 0, 0);
                checkOutDate.setHours(0, 0, 0, 0);

                if (checkInDate <= today && checkOutDate >= today) {
                    currentCheckIns.push(booking);
                } else if (checkInDate > today) {
                    upcomingBookings.push(booking);
                }
            });

            return {
                upcomingBookings: upcomingBookings.sort((a, b) => new Date(a.checkIn) - new Date(b.checkIn)),
                currentCheckIns: currentCheckIns.sort((a, b) => new Date(a.checkIn) - new Date(b.checkIn))
            };
        } catch (error) {
            console.error('Error fetching hotel overview:', error);
            return { upcomingBookings: [], currentCheckIns: [] };
        }
    }

    static async getMonthBirthdays(authorizedFetch) {
        try {
            const response = await authorizedFetch(`${API_URL}/api/dogs?limit=1000`);
            if (!response.ok) throw new Error(await response.text());
            const dogsData = await response.json();
            const dogs = dogsData.data ? dogsData.data : (Array.isArray(dogsData) ? dogsData : []);

            const now = new Date();
            const thisMonth = now.getMonth();
            const today = now.getDate();

            return dogs
                .filter(dog => {
                    if (!dog.dateOfBirth) return false;
                    const dob = new Date(dog.dateOfBirth);
                    return !isNaN(dob.getTime()) && dob.getMonth() === thisMonth;
                })
                .map(dog => {
                    const dob = new Date(dog.dateOfBirth);
                    return {
                        dogId: dog._id,
                        name: dog.name,
                        breed: dog.breed || '',
                        pictureUrl: dog.pictureUrl || dog.picture || null,
                        ownerName: dog.owners?.[0]?.name || '',
                        day: dob.getDate(),
                        isToday: dob.getDate() === today,
                    };
                })
                .sort((a, b) => a.day - b.day);
        } catch (error) {
            console.error('Error fetching month birthdays:', error);
            return [];
        }
    }

    static async getUpcomingTrials(authorizedFetch) {
        try {
            const res = await authorizedFetch(`${API_URL}/api/daycare/trials/upcoming`);
            if (!res.ok) throw new Error(await res.text());
            return await res.json();
        } catch (error) {
            console.error('Error fetching upcoming trial bookings:', error);
            return [];
        }
    }

    static async getDashboardOverview(authorizedFetch) {
        try {
            const [
                todaysVetAppointments,
                upcomingVetAppointments,
                upcomingVaccinations,
                todaysVisits,
                hotelOverview,
                transportationToday,
                lunchToday,
                upcomingTrialsData,
                monthBirthdays,
            ] = await Promise.all([
                this.getTodaysVetAppointments(authorizedFetch),
                this.getUpcomingVetAppointments(authorizedFetch),
                this.getUpcomingVaccinations(authorizedFetch),
                DaycareService.getTodaysVisits(authorizedFetch),
                this.getHotelOverview(authorizedFetch),
                this.getTodayTransportation(authorizedFetch),
                this.getTodayLunch(authorizedFetch),
                this.getUpcomingTrials(authorizedFetch),
                this.getMonthBirthdays(authorizedFetch),
            ]);

            const daycareCheckedIn = todaysVisits.filter(visit => visit.checkInTime && !visit.checkOutTime);

            // Filter transportation and lunch to dogs actually at the facility today
            const todayDogIds = new Set(todaysVisits.map(v => v.dog?._id?.toString()).filter(Boolean));
            const hotelDogIds = new Set(hotelOverview.currentCheckIns.map(b => b.dog?._id?.toString()).filter(Boolean));
            const facilityDogIds = new Set([...todayDogIds, ...hotelDogIds]);

            const filteredTransportation = facilityDogIds.size > 0
                ? transportationToday.filter(t => facilityDogIds.has(t.dogId?.toString()))
                : transportationToday;
            const filteredLunch = facilityDogIds.size > 0
                ? lunchToday.filter(l => facilityDogIds.has(l.dogId?.toString()))
                : lunchToday;

            const now = new Date();
            const todayString = [now.getFullYear(), String(now.getMonth() + 1).padStart(2, '0'), String(now.getDate()).padStart(2, '0')].join('-');
            
            const todaysTrials = upcomingTrialsData.filter(trial => {
                if (!trial.scheduledDate) return false;
                const d = new Date(trial.scheduledDate);
                if (isNaN(d.getTime())) return false;
                return [d.getFullYear(), String(d.getMonth() + 1).padStart(2, '0'), String(d.getDate()).padStart(2, '0')].join('-') === todayString;
            });

            const futureTrials = upcomingTrialsData.filter(trial => {
                if (!trial.scheduledDate) return false;
                const d = new Date(trial.scheduledDate);
                if (isNaN(d.getTime())) return false;
                return [d.getFullYear(), String(d.getMonth() + 1).padStart(2, '0'), String(d.getDate()).padStart(2, '0')].join('-') > todayString;
            });

            const todos = [];

            if (todaysVetAppointments.length > 0) {
                todos.push({
                    id: 'todays-vet-appointments',
                    type: 'vet',
                    title: `${todaysVetAppointments.length} Vet Appointment${todaysVetAppointments.length > 1 ? 's' : ''} Today`,
                    description: `You have ${todaysVetAppointments.length} veterinary appointment${todaysVetAppointments.length > 1 ? 's' : ''} scheduled for today`,
                    priority: 'high',
                    link: '/vet'
                });
            }

            if (todaysTrials.length > 0) {
                todos.push({
                    id: 'trials-today',
                    type: 'trial',
                    title: `${todaysTrials.length} Prueba${todaysTrials.length > 1 ? 's' : ''} de hoy`,
                    description: `Tienes ${todaysTrials.length} prueba${todaysTrials.length > 1 ? 's' : ''} de guardería programada${todaysTrials.length > 1 ? 's' : ''} para hoy`,
                    priority: 'high',
                    link: '/daycare'
                });
            }

            const upcomingOnlyVetAppointments = upcomingVetAppointments.filter(appointment => {
                const dateValue = appointment.date || appointment.appointmentDate;
                if (!dateValue) return false;
                const appointmentDate = new Date(dateValue);
                if (isNaN(appointmentDate.getTime())) return false;
                const today = new Date();
                appointmentDate.setHours(0, 0, 0, 0);
                today.setHours(0, 0, 0, 0);
                return appointmentDate.getTime() > today.getTime();
            });

            if (upcomingOnlyVetAppointments.length > 0) {
                todos.push({
                    id: 'upcoming-vet-appointments',
                    type: 'vet',
                    title: `${upcomingOnlyVetAppointments.length} Upcoming Vet Appointment${upcomingOnlyVetAppointments.length > 1 ? 's' : ''}`,
                    description: `${upcomingOnlyVetAppointments.length} veterinary appointment${upcomingOnlyVetAppointments.length > 1 ? 's' : ''} scheduled in the next 7 days`,
                    priority: 'medium',
                    link: '/vet'
                });
            }

            if (daycareCheckedIn.length > 0) {
                todos.push({
                    id: 'daycare-checkouts-pending',
                    type: 'daycare',
                    title: `${daycareCheckedIn.length} Dog${daycareCheckedIn.length > 1 ? 's' : ''} to Check Out`,
                    description: `${daycareCheckedIn.length} dog${daycareCheckedIn.length > 1 ? 's are' : ' is'} still checked in to daycare`,
                    priority: 'medium',
                    link: '/daycare'
                });
            }

            const todaysCheckIns = hotelOverview.upcomingBookings.filter(booking => {
                if (!booking.checkIn) return false;
                const checkInDate = new Date(booking.checkIn);
                if (isNaN(checkInDate.getTime())) return false;
                const today = new Date();
                checkInDate.setHours(0, 0, 0, 0);
                today.setHours(0, 0, 0, 0);
                return checkInDate.getTime() === today.getTime();
            });

            if (todaysCheckIns.length > 0) {
                todos.push({
                    id: 'hotel-checkins-today',
                    type: 'hotel',
                    title: `${todaysCheckIns.length} Hotel Check-in${todaysCheckIns.length > 1 ? 's' : ''} Today`,
                    description: `${todaysCheckIns.length} dog${todaysCheckIns.length > 1 ? 's are' : ' is'} scheduled to check in to the hotel today`,
                    priority: 'high',
                    link: '/hotel'
                });
            }

            return {
                todos,
                vetAppointments: {
                    today: todaysVetAppointments.slice(0, 5),
                    upcoming: upcomingVetAppointments.slice(0, 5)
                },
                daycare: {
                    checkedIn: daycareCheckedIn.slice(0, 8)
                },
                hotel: {
                    upcomingBookings: hotelOverview.upcomingBookings.slice(0, 5),
                    currentCheckIns: hotelOverview.currentCheckIns.slice(0, 8)
                },
                transportation: filteredTransportation,
                lunch: filteredLunch,
                trials: todaysTrials,
                upcomingTrials: futureTrials.slice(0, 5),
                upcomingVaccinations,
                birthdays: monthBirthdays,
            };
        } catch (error) {
            console.error('Error fetching dashboard overview:', error);
            return {
                todos: [],
                vetAppointments: { today: [], upcoming: [] },
                daycare: { checkedIn: [] },
                hotel: { upcomingBookings: [], currentCheckIns: [] },
                transportation: [],
                lunch: [],
                trials: [],
                upcomingVaccinations: [],
                birthdays: [],
            };
        }
    }

    /**
     * What the previous day left open: daycare visits without checkout, vet and
     * grooming still open, trips never closed, hotel stays without check-in or
     * checkout, unresolved trial days.
     * @param {Function} authorizedFetch - Authenticated fetch function
     * @returns {Promise<Object|null>} Summary, or null when it can't be loaded
     */
    static async getLooseEnds(authorizedFetch) {
        try {
            const res = await authorizedFetch(`${API_URL}/api/dashboard/loose-ends`);
            if (!res.ok) throw new Error(await res.text());
            return await res.json();
        } catch (error) {
            // The summary is a reminder, not something worth breaking the dashboard over
            console.error('Error fetching loose ends summary:', error);
            return null;
        }
    }
}
